import type { FormFieldResolverOptions } from "@primevue/forms";
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber";

export function resolvePhoneNumber({value, name}: FormFieldResolverOptions, label?: string): {errors: { message: string}[]} {
  label ??= name ?? "Phone number";

  if (value == undefined || value.trim() == "") { return { errors: [] }; }

  const errors = [];
  const phoneUtil = PhoneNumberUtil.getInstance();
  try {
    const number = phoneUtil.parse(value, "CA");
    const result = phoneUtil.isPossibleNumberWithReason(number);
    switch (result) {
      case PhoneNumberUtil.ValidationResult.INVALID_COUNTRY_CODE:
        errors.push({message: `${label} has an invalid country code`});
        break;
      case PhoneNumberUtil.ValidationResult.INVALID_LENGTH:
        errors.push({message: `${label} is an invalid phone number`});
        break;
      case PhoneNumberUtil.ValidationResult.TOO_LONG:
        errors.push({message: `${label} is too long`});
        break;
      case PhoneNumberUtil.ValidationResult.TOO_SHORT:
        errors.push({message: `${label} is too short`});
        break;
      case PhoneNumberUtil.ValidationResult.IS_POSSIBLE:
      case PhoneNumberUtil.ValidationResult.IS_POSSIBLE_LOCAL_ONLY:
        if (!phoneUtil.isValidNumber(number)) {
          errors.push({message: `${label} is an invalid phone number`});
        }
    }
  } catch (err) {
    let message = (err as Error).message;
    if (message == "The string supplied did not seem to be a phone number") {
      message = `${label} is an invalid phone number`;
    }
    errors.push({message: message});
  }
  return { errors };
}

export function formatPhoneNumber(value: string | undefined): string | undefined {
  if (value == undefined || value.trim() == "") { return value; }

  const phoneUtil = PhoneNumberUtil.getInstance();
  try {
    const number = phoneUtil.parse(value, "CA");
    const result = phoneUtil.isPossibleNumberWithReason(number);
    switch (result) {
      case PhoneNumberUtil.ValidationResult.IS_POSSIBLE:
      case PhoneNumberUtil.ValidationResult.IS_POSSIBLE_LOCAL_ONLY:
        if (phoneUtil.isValidNumber(number)) {
          const normalized = phoneUtil.format(number, PhoneNumberFormat.INTERNATIONAL);
          if (!value.startsWith(normalized)) {
            return normalized;
          }
        }
    }
  } catch {}
  return value;
}

export function isPostalCodeValid(value: string): boolean {
  // https://stackoverflow.com/a/7185241
  return /(^\d{5}([ \-]?\d{4})?$)|(^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1}[ \-]?\d{1}[A-Z]{1}\d{1}$)/i.test(value);
}

export function formatPostalCode(value: string): string {
  // https://stackoverflow.com/a/7185241
  const match: RegExpExecArray | null = (
  // 1 2               3           4 5                                           6
    /(^(\d{5})(?:[ \-]?(\d{4}))?$)|(^([ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1})[ \-]?(\d{1}[A-Z]{1}\d{1})$)/i.exec(value.trim())
  );
  if (match == null) {
    return value;
  } else if (match[1] !== undefined && match[3] === undefined) {
    // 5 digit us zip code
    return match[2];
  } else if (match[1] !== undefined && match[3] !== undefined) {
    // 9 digit us zip code
    return `${match[2]}-${match[3]}`;
  } else if (match[4] !== undefined) {
    // canadian postal code
    return `${match[5].toUpperCase()} ${match[6].toUpperCase()}`;
  } else {
    return value;
  }
}

export function isUrlValid(url: string): boolean {
  // https://mathiasbynens.be/demo/url-regex
  return /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00a1-\uffff][a-z0-9\u00a1-\uffff_-]{0,62})?[a-z0-9\u00a1-\uffff]\.)+(?:[a-z\u00a1-\uffff]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?$/.test(url);
}

export function isEmailValid(email: string): boolean {
  return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
}
